import { RiFeedbackLine } from "@remixicon/react";
import classNames from "classnames";
import React, { useMemo } from "react";
import {
    ArticleFeedback, ArticleFeedbackType, GetArticleDocument, useStoreArticleFeedbackMutation,
} from "../../../../__generated__/api";
import Alert from "../../../../components/theme/alert/Alert";
import Button from "../../../../components/theme/button/Button";
import Icon from "../../../../components/theme/icon/Icon";
import Text from "../../../../components/theme/text/Text";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import Styles from "./Feedback.module.scss";

const LOCAL_STORAGE_KEY = "feedback-voted";

export default function Feedback({ id, feedback }: {id: string; feedback: ArticleFeedback | undefined}) {
    const [saveFeedback] = useStoreArticleFeedbackMutation();
    const votedList = useMemo(() => new Set(JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) ?? "[]")), []);

    const vote = (type: ArticleFeedbackType) => {
        LoggingHandler.instance.wrap(async () => {
            votedList.add(id);
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(Array.from(votedList)));
            await saveFeedback({
                refetchQueries: [
                    { query: GetArticleDocument, variables: { id } },
                ],
                variables: {
                    feedback: {
                        feedback: type,
                        id,
                    },
                },
            });
        });
    };
    return (
        <div className={classNames(Styles["feedback-block"])}>
            <div className={Styles["feedback-block-header"]}>
                <Icon
                    background="primary"
                    size="xlarge"
                    isRounded
                    variant="primary"
                    className={Styles["feedback-bar-icon"]}
                    icon={<RiFeedbackLine />}
                />
                <Text textVariant="body-1-semibold" colorVariant="text-title">
                    Was this article helpful?
                </Text>
            </div>
            <Text className={Styles["feedback-block-caption"]} textVariant="body-2-regular" colorVariant="body-secondary">
                {(feedback?.helpfulCount ?? 0) > 0 && (
                    `${feedback?.helpfulCount ?? 0} people found this article helpful. `
                )}
                {!votedList.has(id) && (
                    <>
                        Let us know if this article was
                        helpful to you. Your feedback helps us improve our content.
                    </>
                )}
            </Text>

            {votedList.has(id) ? (
                <Alert variant="disabled">
                    Thank you for your feedback!
                </Alert>
            ) : (
                <div className={Styles["feedback-block-buttons"]}>
                    <Button
                        variant="outline-gray"
                        metricPath={[LoggingHandler.Metrics.ARTICLES, "feedback", ArticleFeedbackType.Helpful]}
                        onClick={() => vote(ArticleFeedbackType.Helpful)}
                    >
                        👍 Yes
                    </Button>
                    <Button
                        variant="outline-gray"
                        metricPath={[LoggingHandler.Metrics.ARTICLES, "feedback", ArticleFeedbackType.NotHelpful]}
                        onClick={() => vote(ArticleFeedbackType.NotHelpful)}
                    >
                        👎 No
                    </Button>
                </div>
            )}

        </div>
    );
}
